<template>
  <div>
    <div>
      <div aria-live="polite">
        <h2 class="category-header">{{ categoryData.skill_name }}</h2>
        <RadioRow
          :key="`${selectedGradeValue}${selectedCategoryValue}${selectedLevelValue}`"
          :columns="levelsLayoutNumColumns"
          :option-set="levelOptionSet"
          :default-options="[selectedLevelValue]"
          :label="levelLabelOrientation"
          @selectedOptions="options => updateSelectedLevelValue(options[0])"
        />
      </div>
      <div class="statement-content" aria-live="polite">
        <CopyTrackWrapper
          type="presentlevel"
          :title="currentStatementData.skill_name"
          :uuid="currentStatementData.skill_uuid"
          :level="selectedLevelValue">
          <EditableWikiTextArea
            class="gb-text-3 pl-editable-textarea font-weight-normal"
            :value="currentStatementText"
            :reset-value="defaultStatementText"
            :resources="currentResources"
            @textReset="saveEdit"
            @textEdited="handleTextEdit"
            @activatedEditText="() => {}"
            :content-title="currentStatementData.skill_name"
          />
        </CopyTrackWrapper>

        <!-- Assessments -->
        <section v-if="currentResources && Object.keys(currentResources).length">
          <h3 class="text-pl-purple">Assessment Resources</h3>
          <div class="statement-resources">
            <ResourceTile
              v-for="resource in currentResources"
              :key="resource.uuid"
              v-bind="resource"/>
          </div>
        </section>

        <!-- Standard information section -->
        <section
          v-if="Object.keys(currentStandardsBySetCode).length > 0"
          class="mdc-card__supporting-text pl-section">
          <div
            v-for="(standards, standardSet) in currentStandardsBySetCode"
            :key="standardSet">
            <h3 class="text-primary text-pl-purple">{{ standardHeading(standardSet) }}</h3>
            <CopyTrackWrapper
              v-for="s in standards"
              :key="s.standard_long_ref"
              class="pl-subsection text-muted"
              type="pl-standard"
              :title="s.standard_long_ref"
              :uuid="s.standard_long_ref">
              <PresentLevelsStandardView
                :set-code="s.standard_set_code"
                :long-ref="s.standard_long_ref"
                :short-ref="s.standard_short_ref"
                :standard-text="s.standard_text"
                :info-obj="s"/>
            </CopyTrackWrapper>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import {headingsForStandardSetCode} from '@/components/goals/common/standard_headings';
import redundantRouteMixin from '@/components/RedundantRouteMixin';
import { mapGetters } from 'vuex'
import RadioRow from '@/components/presentlevels/RadioRow.vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import EditableWikiTextArea from '@/components/EditableWikiTextArea.vue'
import { populatePlaceholderText } from '@/api/presentlevels'
import PresentLevelsStandardView from '@/standards/PresentLevelsStandardView.vue'
import ResourceTile from '@/components/goals/ResourceTile.vue'
import { SUBJECT_TYPES } from '@/util/academic_subjects'

export default {
  name: 'BrowsePresentLevelsResults',
  components: { ResourceTile, PresentLevelsStandardView, EditableWikiTextArea, CopyTrackWrapper, RadioRow },
  mixins: [redundantRouteMixin],
  data() {
    return {
      selectedLevelValueInternal: this.selectedLevelValue || '',
      editedStatements: {}
    }
  },
  props: {
    categoryData: {
      type: Object,
      default() {
        return {}
      }
    },
    statementOptions: {
      type: Array,
      required: true
    },
    selectedLevelValue: {
      type: String,
      required: true
    },
    selectedGradeValue: {
      type: String,
      required: true
    },
    selectedCategoryValue: {
      type: String,
      required: true
    },
    selectedSubjectType: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters('contentProfile', ['visibleStandardSets', 'profileId']),
    currentStatementData() {
      if (this.statementOptions && this.statementOptions.length && this.selectedLevelValue) {
        return this.statementOptions.find(statement => statement.level_label === this.selectedLevelValue)
      }
      return ''
    },
    currentStatementText() {
      return this.editedStatements[this.selectedLevelValue] || this.defaultStatementText
    },
    defaultStatementText() {
      if (!this.currentStatementData) {
        return ''
      }
      let today = new Date()

      return populatePlaceholderText(this.currentStatementData.statement_text, {
        studentName: '(Name)',
        studentGrade: this.selectedLevelValue,
        dateStr: [today.getMonth() + 1, '/', today.getDate(), '/', today.getFullYear()].join('')
      })
    },
    currentResources() {
      let resourceMap = {}
      if (!this.currentStatementData || !this.currentStatementData.resource_docs) {
        return resourceMap
      }
      for (let resource of this.currentStatementData.resource_docs) {
        if (resource.uuid && !resource.error) {
          resourceMap[resource.uuid] = resource
        }
      }
      return resourceMap
    },
    currentStandardsBySetCode() {
      if (!this.currentStatementData || !this.currentStatementData.standard_docs) {
        return []
      }
      let filteredStandards = this.currentStatementData.standard_docs.filter(standard => {
        return (
          standard.standard_long_ref &&
          standard.standard_long_ref.trim() !== ''
          && this.visibleStandardSets.includes(standard.standard_set_code)
        )
      })
      const standardsBySetCode = {}
      for (let standard of filteredStandards) {
        if (!standardsBySetCode[standard.standard_set_code]) {
          standardsBySetCode[standard.standard_set_code] = []
        }
        standardsBySetCode[standard.standard_set_code].push(standard)
      }
      return standardsBySetCode
    },
    levelOptionSet() {
      return this.statementOptions.map(statement => {
        statement.label = statement.level_label
        return statement
      })
    },
    levelsLayoutNumColumns() {
      switch (this.selectedSubjectType) {
        case SUBJECT_TYPES.ACADEMIC:
          return 14
        case SUBJECT_TYPES.ALT:
          return 6
        default:
          return 2
      }
    },
    levelLabelOrientation() {
      switch (this.selectedSubjectType) {
        case SUBJECT_TYPES.ACADEMIC:
        case SUBJECT_TYPES.ALT:
          return 'top'
        default:
          return 'right'
      }
    }
  },
  methods: {
    updateSelectedLevelValue(event) {
      this.$emit('update:selectedLevelValue', event)
      this.$sendEvent('Selected Browse Present Levels Level')
    },
    handleTextEdit(text) {
      if (text !== this.currentStatementText) {
        this.$sendEvent('Edited Browse Present Levels Statement', {'edited_text': text})
        this.saveEdit(text)
      }
    },
    saveEdit(text) {
      if (text) {
        const updatedEditedStatements = Object.assign({}, this.editedStatements)
        updatedEditedStatements[this.selectedLevelValue] = text
        this.editedStatements = updatedEditedStatements
      }
    },
    standardHeading(standardSetCode) {
      return headingsForStandardSetCode(standardSetCode, {
        profileId: this.profileId,
      })
    },
    verifyStatementUnlocked() {
      if (this.currentStatementData.locked) {
        const lowestUnlockedStatement = this.statementOptions.find(statement => !statement.locked)
        this.updateSelectedLevelValue(lowestUnlockedStatement.level_label)
      }
    }
  },
  watch: {
    selectedLevelValue(newValue) {
      this.verifyStatementUnlocked()
      this.selectedLevelValueInternal = newValue
    },
    statementOptions() {
      this.verifyStatementUnlocked()
      this.editedStatements = {}
    },
  },
  mounted() {
    this.verifyStatementUnlocked()
  }
}
</script>

<style scoped lang='less'>
@import "~@/variables.less";
.statement-content{
  border: 1px solid @gray-base;
  border-radius: 0.5rem;
  padding: 2rem;

  section{
    margin-top: 2rem;
  }

  .statement-resources {
    display: flex;
    flex-wrap: wrap;
  }
}
.category-header {
  margin-top: 0
}
.radio-row {
  margin-bottom: 1rem;
}
</style>
